import React from 'react';
import dayjs from 'dayjs';
import { Button } from 'antd';
import {
    CheckOutlined,
    CloseOutlined,
    UserOutlined,
    ClockCircleOutlined,
    CameraOutlined,
    DisconnectOutlined,
} from '@ant-design/icons';
import { ReceptionFragmentProps } from 'fragments/reception/interfaces';
import { useReceptionController } from 'fragments/reception/reception.controller';
import { ReceptionScanner } from 'fragments/reception/reception-scanner';
import { isReused, summarizeUses } from 'fragments/reception/uses-summary';
import { formatValidity } from 'fragments/reception/validity-label';
import { useTranslator } from 'tools/view-hooks/translator-hook';
import 'fragments/reception/reception.scss';

export const ReceptionFragment: React.FC<ReceptionFragmentProps> = (props) => {
    const { translate } = useTranslator();
    const useController = props.useController ?? useReceptionController;
    const c = useController();

    if (c.stage === 'scanner' || c.stage === 'verifying') {
        return (
            <ReceptionScanner
                onDecode={c.onScan}
                onPermissionError={c.onPermissionError}
                armed={c.stage === 'scanner'}
            />
        );
    }

    if (c.stage === 'permission') {
        return (
            <div className={'reception'}>
                <div className={'reception-body'}>
                    <div className={'reception-neutralbadge'}>
                        <CameraOutlined style={{ fontSize: 36, color: '#5b6070' }} aria-hidden />
                    </div>
                    <div className={'reception-reason'}>{translate({ key: 'reception.perm-title' })}</div>
                    <div className={'reception-reason-sub'}>{translate({ key: 'reception.perm-sub' })}</div>
                </div>
                <div className={'reception-footer'}>
                    <Button type={'primary'} className={'reception-btn'} onClick={c.onRetryPermission}>
                        {translate({ key: 'reception.perm-retry' })}
                    </Button>
                </div>
            </div>
        );
    }

    if (c.stage === 'offline') {
        return (
            <div className={'reception'}>
                <div className={'reception-body'}>
                    <div className={'reception-neutralbadge'}>
                        <DisconnectOutlined style={{ fontSize: 36, color: '#5b6070' }} aria-hidden />
                    </div>
                    <div className={'reception-reason'} role={'alert'}>
                        {translate({ key: 'reception.offline-title' })}
                    </div>
                    <div className={'reception-reason-sub'}>{translate({ key: 'reception.offline-sub' })}</div>
                </div>
                <div className={'reception-footer'}>
                    <Button
                        type={'primary'}
                        className={'reception-btn'}
                        loading={c.isRetrying}
                        onClick={c.onRetryVerify}
                    >
                        {translate({ key: 'reception.offline-retry' })}
                    </Button>
                    <Button type={'text'} className={'reception-btn-secondary'} onClick={c.onBackToScanner}>
                        {translate({ key: 'reception.offline-back' })}
                    </Button>
                </div>
            </div>
        );
    }

    if (c.stage === 'invalid') {
        return (
            <div className={'reception'}>
                <div className={'reception-hero reception-hero--danger'}>
                    <div className={'reception-hero-badge reception-hero-badge--danger'}>
                        <CloseOutlined style={{ fontSize: 36, color: '#fff' }} aria-hidden />
                    </div>
                    <div className={'reception-hero-title reception-hero-title--danger'}>
                        {translate({ key: 'reception.invalid-title' })}
                    </div>
                </div>
                <div className={'reception-body'}>
                    <div className={'reception-reason'}>{translate({ key: c.errorKey })}</div>
                    <div className={'reception-reason-sub'}>{translate({ key: 'reception.invalid-sub' })}</div>
                </div>
                <div className={'reception-footer'}>
                    <Button type={'primary'} className={'reception-btn'} onClick={c.onScanNext}>
                        {translate({ key: 'reception.scan-other' })}
                    </Button>
                </div>
            </div>
        );
    }

    if (c.stage === 'registered') {
        return (
            <div className={'reception'}>
                <div className={'reception-body'}>
                    <div className={'reception-hero-badge reception-hero-badge--ok'} style={{ width: 84, height: 84 }}>
                        <CheckOutlined style={{ fontSize: 42, color: '#fff' }} aria-hidden />
                    </div>
                    <div className={'reception-reason'}>{translate({ key: 'reception.registered-title' })}</div>
                    <div className={'reception-strong'}>{c.verify?.visitor_name}</div>
                    <div className={'reception-muted'}>
                        {c.confirmedAt ? dayjs(c.confirmedAt).format('DD/MM · HH:mm') : ''}
                    </div>
                </div>
                <div className={'reception-footer'}>
                    <Button type={'primary'} className={'reception-btn'} onClick={c.onScanNext}>
                        {translate({ key: 'reception.scan-next' })}
                    </Button>
                </div>
            </div>
        );
    }

    // stage === 'valid'
    const validity = formatValidity(c.verify?.valid_from, c.verify?.valid_until);
    const reused = isReused(c.verify);
    const summary = reused && c.verify ? summarizeUses(c.verify) : undefined;
    const tone = reused ? 'warn' : 'ok';
    return (
        <div className={'reception'}>
            <div className={`reception-hero reception-hero--${tone}`}>
                <div className={`reception-hero-badge reception-hero-badge--${tone}`}>
                    <CheckOutlined style={{ fontSize: 36, color: '#fff' }} aria-hidden />
                </div>
                <div className={`reception-hero-title reception-hero-title--${tone}`}>
                    {translate({ key: reused ? 'reception.valid-reused-title' : 'reception.valid-title' })}
                </div>
            </div>
            <div className={'reception-body'}>
                <div className={'reception-card'}>
                    <UserOutlined style={{ fontSize: 32, color: '#7c5fbc' }} aria-hidden />
                    <div className={'reception-visitor'}>{c.verify?.visitor_name}</div>
                    {c.verify?.site_name && <div className={'reception-site'}>{c.verify.site_name}</div>}
                    {validity && (
                        <div className={'reception-validchip'}>
                            <ClockCircleOutlined aria-hidden />
                            <span>{validity}</span>
                        </div>
                    )}
                    {summary && (
                        <div className={'reception-usehistory'}>
                            <div className={'reception-usehistory-count'}>{summary.count}</div>
                            {summary.last && <div className={'reception-usehistory-last'}>{summary.last}</div>}
                            {summary.cap && <div className={'reception-usehistory-cap'}>{summary.cap}</div>}
                        </div>
                    )}
                </div>
            </div>
            <div className={'reception-footer'}>
                <Button
                    type={'primary'}
                    className={'reception-btn reception-btn--ok'}
                    loading={c.isConfirming}
                    icon={<CheckOutlined />}
                    onClick={c.onConfirm}
                >
                    {translate({ key: 'reception.confirm' })}
                </Button>
                <Button type={'text'} className={'reception-btn-secondary'} onClick={c.onCancel}>
                    {translate({ key: 'reception.cancel' })}
                </Button>
            </div>
        </div>
    );
};
